// Renders the 8-part CourtScribe summary model (see courtSummaryDoc.js) as a PDF
// using the shared court letterhead chrome (see courtPdfChrome.js).
import {
  PAGE,
  CONTENT_WIDTH,
  COLORS,
  PdfWriter,
  getLogoDataUrl,
  renderLetterhead,
  jsPDF,
} from './courtPdfChrome';

const renderPart7 = (writer, doc, part7) => {
  writer.text(part7.intro, { size: 8, style: 'italic', color: COLORS.muted });
  const boxWidth = CONTENT_WIDTH;
  const drawBlock = (title, statement, signerLabels) => {
    writer.ensure(46);
    const top = writer.y;
    doc.setDrawColor(...COLORS.line);
    doc.setLineWidth(0.3);
    writer.subHeading(title);
    writer.text(statement, { size: 9, style: 'italic' });
    signerLabels.forEach((label) => {
      writer.ensure(8);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(...COLORS.ink);
      doc.text(`${label} _________________________________________`, PAGE.margin + 4, writer.y);
      writer.y += 6.5;
    });
    doc.rect(PAGE.margin, top, boxWidth, writer.y - top + 2);
    writer.y += 6;
  };
  drawBlock('A.  Verification by the Court Registrar', part7.registrarStatement, [
    'Court Registrar — Name', 'Signature', 'Date',
  ]);
  drawBlock('B.  Approval by the Presiding Judge', part7.judgeStatement, [
    'Presiding Judge — Name', 'Signature', 'Date',
  ]);
  writer.ensure(24);
  const stampTop = writer.y;
  doc.setDrawColor(...COLORS.line);
  doc.rect(PAGE.margin, stampTop, CONTENT_WIDTH / 2 - 2, 20);
  doc.rect(PAGE.margin + CONTENT_WIDTH / 2 + 2, stampTop, CONTENT_WIDTH / 2 - 2, 20);
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.muted);
  doc.text('OFFICIAL COURT STAMP / SEAL\n— affix on certification', PAGE.margin + 4, stampTop + 6, { maxWidth: CONTENT_WIDTH / 2 - 8 });
  doc.text('CERTIFICATION MARK / SERIAL NO.\n— enter on certification', PAGE.margin + CONTENT_WIDTH / 2 + 4, stampTop + 6, { maxWidth: CONTENT_WIDTH / 2 - 8 });
  writer.y = stampTop + 24;
};

/** Render the summary model into a jsPDF instance and return the doc. */
export const renderCourtSummaryPdf = (model, { logoDataUrl } = {}) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  doc.setProperties({ title: `${model.meta.caseTitle} — AI-Generated Summary` });
  const writer = new PdfWriter(doc);
  writer.startPage(model.meta);
  renderLetterhead(writer, doc, model.meta, logoDataUrl);

  writer.partBanner('PART 1 — CASE DETAILS');
  writer.labelTable(model.part1.rows);

  writer.partBanner('PART 2 — PARTICIPANTS');
  writer.headTable(
    model.part2.head,
    model.part2.rows.map((p) => [p.role, p.name, p.notes]),
    [42, 52, CONTENT_WIDTH - 94]
  );

  writer.partBanner('PART 3 — TRANSCRIPT GENERATION DETAILS');
  writer.labelTable(model.part3.rows);
  writer.text(model.part3.disclaimer, { size: 8, style: 'italic', color: COLORS.muted });

  writer.partBanner('PART 4 — SUMMARY');
  writer.subHeading('Purpose');
  writer.text(model.part4.purpose, { size: 9 });
  writer.subHeading('Summary of Proceedings');
  writer.text(model.part4.narrative, { size: 9 });
  if (model.part4.keyPoints.length) {
    writer.subHeading('Key Points');
    writer.bullets(model.part4.keyPoints);
  }
  writer.subHeading('Decisions and Orders');
  writer.bullets(model.part4.decisions);
  if (model.part4.actionItems.length) {
    writer.subHeading('Action Items');
    writer.bullets(model.part4.actionItems);
  }
  writer.subHeading('Next Date');
  writer.text(model.part4.nextDate, { size: 9 });
  if (model.part4.openQuestions.length) {
    writer.subHeading('Open Questions');
    writer.bullets(model.part4.openQuestions);
  }

  writer.partBanner('PART 5 — TRANSCRIPT');
  writer.text(model.part5.note, { size: 8, style: 'italic', color: COLORS.muted });
  writer.headTable(
    model.part5.head,
    model.part5.rows.map((r) => [r.time, r.speaker, r.proceedings]),
    [22, 40, CONTENT_WIDTH - 62]
  );
  writer.text(model.part5.conventions, { size: 8, style: 'italic', color: COLORS.muted });

  writer.partBanner('PART 6 — REVIEW AND CORRECTIONS LOG');
  writer.text(model.part6.intro, { size: 8, style: 'italic', color: COLORS.muted });
  if (model.part6.rows.length) {
    writer.headTable(
      model.part6.head,
      model.part6.rows.map((r) => [String(r.item), r.correction, r.reason, r.initials]),
      [12, CONTENT_WIDTH - 96, 60, 24]
    );
    writer.subHeading('Items flagged by the AI system for checking against the recording');
    writer.bullets(model.part6.rows.map((r) => r.correction));
  } else {
    writer.text('No corrections or flags recorded against this draft yet.', { size: 9 });
  }
  writer.subHeading('Reviewer notes');
  writer.bullets(model.part6.reviewerNotes);

  writer.partBanner('PART 7 — VERIFICATION AND CERTIFICATION');
  renderPart7(writer, doc, model.part7);

  writer.partBanner('PART 8 — DISTRIBUTION AND HANDLING');
  writer.labelTable(model.part8.rows);

  writer.finish();
  return doc;
};

/** Rasterize the brand logo and export the summary model as a PDF download. */
export const downloadCourtSummaryPdf = async (model, filename) => {
  const logoDataUrl = await getLogoDataUrl();
  const doc = renderCourtSummaryPdf(model, { logoDataUrl });
  doc.save(filename);
};
